import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useKeyboardMap, type KeyMap } from '@/app/useKeyboardMap';
import {
  HealthBar,
  HealthDelta,
  HealthTimeline,
  StatusPill,
  DataFreshnessBadge,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  TopologyStrip,
  EmptyState,
  type StatusLevel,
} from '@/lib';
import type { ApiClients, ApiClientConfig } from '@/api/client';
import {
  goAnalyze,
  goAsset,
  goAlert,
  goFleet,
} from '@/app/navigation';
import {
  useAcknowledgeAlert,
  useAssetHealthHistory,
  useAssetTopology,
  useAttentionQueue,
  useSnoozeAlert,
  type AttentionQueueItem,
} from './queries';

export type S1AttentionProps = {
  clients: ApiClients;
  config: ApiClientConfig;
};

const SEVERITY_TO_LEVEL: Record<string, StatusLevel> = {
  critical: 'critical',
  warn: 'warn',
  watch: 'watch',
  healthy: 'healthy',
};

/**
 * S1 Attention. Top-of-queue asset hero with one acknowledgement action
 * and a six-item "Also today" list. Uses MSW-backed mock data for the
 * Phase-3-deferred endpoints; health-history calls the real asset-service.
 */
export function S1Attention({ clients, config }: S1AttentionProps) {
  const navigate = useNavigate();
  const queue = useAttentionQueue(clients);
  const hero = queue.data?.[0];

  // 'A' shortcut routes to Signal Analysis with the top-of-queue asset
  // pre-loaded. When the first attention item is a critical, that's
  // the right default; if there are no criticals it picks whatever
  // the queue ranks first.
  useAnalyzeShortcut(hero?.asset_id, navigate);

  // Has to live BEFORE the early returns so the hook order stays stable
  // across the loading → loaded transitions (React's Rules of Hooks).
  const lastUpdated = useMemo(() => new Date(), [queue.dataUpdatedAt]);

  if (queue.isLoading) {
    return <S1Loading />;
  }
  if (queue.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load attention queue"
        body="Retry, or check the reporting-service status."
        errorCode={String(queue.error)}
        cta={{ label: 'Retry', onClick: () => queue.refetch() }}
      />
    );
  }
  if (!hero) {
    return (
      <EmptyState
        variant="empty"
        title="Fleet is healthy"
        body="Nothing requires your attention right now."
        cta={{ label: 'View fleet', onClick: () => goFleet(navigate) }}
      />
    );
  }

  // Severity tiers drive the page layout. EVERY critical-severity
  // asset deserves the same hero-style treatment (full-width card +
  // health rail + 30d timeline + acknowledge action). The previous
  // design only promoted the #1 item to hero and demoted every other
  // critical to the compact grid below — confusing because an
  // operator scanning "what's on fire" couldn't tell the criticals
  // from the watches at a glance. Non-critical items keep the
  // compact 2-col card grid; they want eyes but don't need the same
  // budget of vertical real estate.
  const criticals = queue.data!.filter((item) => item.severity === 'critical');
  const nonCriticalAll = queue.data!.filter(
    (item) => item.severity !== 'critical',
  );
  // Cap the non-critical grid at 12 so the page doesn't grow
  // unbounded; anything past that flows through to the Fleet view.
  const otherAttention = nonCriticalAll.slice(0, 12);
  const remaining = Math.max(0, nonCriticalAll.length - 12);

  // Multi-critical surfacing: when more than one asset is critical
  // (or the operator just wants a fleet-state glance), an at-a-glance
  // counter above the hero makes it explicit that the hero is one of
  // N criticals, not the only one. Without this strip, the page can
  // read as "everything's under control except this pump" when in
  // fact several assets share the top tier.
  const severityCounts = queue.data!.reduce(
    (acc, item) => {
      const lvl = SEVERITY_TO_LEVEL[item.severity];
      if (lvl === 'critical' || lvl === 'warn' || lvl === 'watch') {
        acc[lvl] += 1;
      }
      return acc;
    },
    { critical: 0, warn: 0, watch: 0 },
  );

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex flex-wrap items-center justify-between gap-sp-3">
        <div className="flex flex-wrap items-center gap-sp-3">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Requires attention
          </p>
          {severityCounts.critical > 0 ? (
            <StatusPill
              level="critical"
              label={`${severityCounts.critical} critical`}
            />
          ) : null}
          {severityCounts.warn > 0 ? (
            <StatusPill
              level="warn"
              label={`${severityCounts.warn} warn`}
            />
          ) : null}
          {severityCounts.watch > 0 ? (
            <StatusPill
              level="watch"
              label={`${severityCounts.watch} watch`}
            />
          ) : null}
        </div>
        <DataFreshnessBadge at={lastUpdated} tone="fresh" />
      </header>

      {criticals.length > 0 ? (
        <section aria-label="Critical assets">
          <header className="mb-sp-3 flex items-baseline justify-between">
            <h2 className="text-fs-statement font-medium text-gray-900">
              Critical{' '}
              <span className="font-mono text-fs-meta text-gray-700">
                ({criticals.length})
              </span>
            </h2>
            <p className="font-mono text-fs-meta text-gray-700">
              Each card needs an acknowledgement
            </p>
          </header>
          <div className="flex flex-col gap-sp-4">
            {criticals.map((item) => (
              <CriticalAssetCard
                key={item.asset_id}
                item={item}
                clients={clients}
                config={config}
                navigate={navigate}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section aria-label="Needs attention">
        <header className="mb-sp-3 flex items-baseline justify-between">
          <h2 className="text-fs-statement font-medium text-gray-900">
            {criticals.length > 0 ? 'Also' : 'Needs'} attention{' '}
            <span className="font-mono text-fs-meta text-gray-700">
              ({otherAttention.length})
            </span>
          </h2>
          <TextButton
            label={remaining > 0 ? `See all (${remaining} more)` : 'See fleet'}
            onClick={() => goFleet(navigate)}
          />
        </header>
        {otherAttention.length === 0 ? (
          <EmptyState
            variant="empty"
            title="Nothing else flagged"
            body="The fleet is otherwise healthy."
          />
        ) : (
          <ul className="grid gap-sp-3 md:grid-cols-2">
            {otherAttention.map((item) => {
              const level = SEVERITY_TO_LEVEL[item.severity] ?? 'info';
              return (
                <li key={item.asset_id}>
                  <article className="flex h-full flex-col gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-4">
                    {/* Card hierarchy: title row with severity chip,
                        then a prominent health rail (large mono +
                        slim bar) — operators sort these cards by
                        health, so it earns the visual weight. RUL +
                        open-alerts compress into a single meta row
                        below. */}
                    <div className="flex items-start justify-between gap-sp-2">
                      <div className="min-w-0">
                        <p className="truncate text-fs-statement font-medium text-gray-900">
                          {item.asset_name}
                        </p>
                        <p className="font-mono text-fs-meta text-gray-700">
                          {item.asset_id} · {item.asset_class} · {item.site}
                        </p>
                      </div>
                      <StatusPill level={level} label={item.severity} />
                    </div>
                    <div className="flex items-baseline justify-between gap-sp-3">
                      <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                        Health
                      </p>
                      <p className="font-mono text-fs-h2 leading-none text-gray-900">
                        {Math.round(item.health)}
                        <span className="text-fs-meta text-gray-700"> / 100</span>
                      </p>
                    </div>
                    <HealthBar
                      size="row"
                      value={item.health}
                      thresholds={{ warn: 40, critical: 20 }}
                    />
                    <dl className="flex flex-wrap items-baseline gap-x-sp-4 gap-y-sp-1 text-fs-meta">
                      <div className="flex items-baseline gap-sp-1">
                        <dt className="text-gray-700">RUL</dt>
                        <dd className="font-mono text-gray-900">{item.rul_days}d</dd>
                      </div>
                      <div className="flex items-baseline gap-sp-1">
                        <dt className="text-gray-700">Open alerts</dt>
                        <dd className="font-mono text-gray-900">{item.open_alerts}</dd>
                      </div>
                    </dl>
                    {item.diagnosis ? (
                      <p className="text-fs-meta text-gray-700 line-clamp-2">
                        {item.diagnosis}
                      </p>
                    ) : null}
                    <div className="mt-auto flex items-center justify-between gap-sp-2 pt-sp-1">
                      <TextButton
                        label="Open asset →"
                        onClick={() => goAsset(navigate, item.asset_id)}
                      />
                      <TextButton
                        label="Analyze ↗"
                        onClick={() => goAnalyze(navigate, item.asset_id)}
                      />
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

// CriticalAssetCard renders a single critical-severity asset at full
// width: identity → health rail → 30-day timeline + diagnosis →
// action row. Each instance owns its own ack/snooze mutations + the
// transient "Alert acknowledged" state so multiple criticals can be
// triaged independently without interfering with each other.
function CriticalAssetCard({
  item,
  clients,
  config,
  navigate,
}: {
  item: AttentionQueueItem;
  clients: ApiClients;
  config: ApiClientConfig;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const history = useAssetHealthHistory(clients, item.asset_id);
  const topology = useAssetTopology(config, item.asset_id);
  const ack = useAcknowledgeAlert(config);
  const snooze = useSnoozeAlert(config);
  const [acked, setAcked] = useState(false);

  const periods = history.data?.periods ?? [];

  return (
    <article className="flex flex-col gap-sp-4 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
      {/* Identity row */}
      <div className="flex items-start justify-between gap-sp-4">
        <div className="min-w-0">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            {item.asset_class} · {item.site} · High criticality
          </p>
          <h3 className="mt-sp-2 text-fs-h2 font-medium text-gray-900">
            {item.asset_name}
          </h3>
          <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
            {item.asset_id}
          </p>
        </div>
        <StatusPill
          level="critical"
          label={`Critical · ${
            item.open_alerts === 1
              ? '1 open alert'
              : `${item.open_alerts} open alerts`
          }`}
        />
      </div>

      {/* Health rail */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-sp-2">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Health
          </p>
          <HealthDelta previous={item.health_prev} current={item.health} />
        </div>
        <div className="mt-sp-2">
          <HealthBar
            size="hero"
            value={item.health}
            previous={item.health_prev}
            thresholds={{ warn: 40, critical: 20 }}
          />
        </div>
        <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
          0 ·{' '}
          <span className="text-critical">crit 20</span>
          {' · '}
          <span className="text-warn-fg">warn 40</span>
          {' · 100'}
        </p>
      </div>

      {/* 30-day timeline + diagnosis */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-sp-2">
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            30-day health
          </p>
          {periods.length > 0 ? (
            <p className="font-mono text-fs-meta text-gray-700">
              {capitalize(periods[0].status)} 30d ago{' '}
              <span className="text-gray-700">→</span>{' '}
              <span className="text-gray-900">
                {capitalize(item.severity)} now ({Math.round(item.health)})
              </span>
            </p>
          ) : null}
        </div>
        <div className="mt-sp-3">
          {history.isLoading ? (
            <HealthTimeline periods={[]} variant="30d" state="loading" />
          ) : periods.length > 0 ? (
            <HealthTimeline
              periods={normalizePeriods(periods)}
              variant="30d"
            />
          ) : (
            <HealthTimeline periods={[]} variant="30d" state="empty" />
          )}
        </div>
        <p className="mt-sp-3 text-fs-body text-gray-700">{item.diagnosis}</p>
      </div>

      {/* Topology — collapsed by default to keep reference info out of
          the way unless the operator explicitly opens it. */}
      {topology.data ? (
        <details className="[&_summary]:cursor-pointer">
          <summary className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700 marker:text-gray-700">
            Topology
          </summary>
          <div className="mt-sp-2 overflow-x-auto opacity-80">
            <TopologyStrip
              nodes={topology.data.nodes}
              focusId={item.asset_id}
              impactedIds={topology.data.impactedIds}
            />
          </div>
        </details>
      ) : null}

      {/* Action row */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="flex flex-wrap items-center gap-sp-3 border-t border-gray-300 pt-sp-3"
      >
        {acked ? (
          <StatusPill level="healthy" label="Alert acknowledged" />
        ) : (
          <PrimaryButton
            label="Acknowledge alert"
            loading={ack.isPending}
            onClick={() => {
              ack.mutate(
                { alertId: item.alert_id },
                { onSuccess: () => setAcked(true) },
              );
            }}
          />
        )}
        <SecondaryButton
          label="View evidence"
          onClick={() => goAlert(navigate, item.alert_id)}
        />
        <TextButton
          label={snooze.isPending ? 'Snoozing…' : 'Snooze 24h'}
          disabled={snooze.isPending}
          onClick={() => snooze.mutate({ alertId: item.alert_id, hours: 24 })}
        />
        <TextButton
          label="Analyze ↗"
          onClick={() => goAnalyze(navigate, item.asset_id)}
        />
        {snooze.isSuccess ? (
          <span role="status" className="font-mono text-fs-meta text-healthy-fg">
            Snoozed 24h
          </span>
        ) : null}
        {snooze.isError ? (
          <p role="alert" className="font-mono text-fs-meta text-critical">
            Couldn't snooze. Try again.
          </p>
        ) : null}
        {ack.isError ? (
          <p role="alert" className="font-mono text-fs-meta text-critical">
            Acknowledge failed. {String(ack.error)}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function S1Loading() {
  return (
    <div className="space-y-sp-6">
      <div className="h-7 w-32 animate-pulse rounded-r-chip bg-gray-100" />
      <div className="h-40 animate-pulse rounded-r-card bg-gray-100" />
      <div className="grid gap-sp-4 lg:grid-cols-[2fr_1fr]">
        <div className="h-48 animate-pulse rounded-r-card bg-gray-100" />
        <div className="h-48 animate-pulse rounded-r-card bg-gray-100" />
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

function normalizePeriods(periods: HealthHistoryPeriod[]) {
  return periods.map((p) => ({ at: new Date(p.at), status: p.status }));
}

type HealthHistoryPeriod = {
  at: string;
  status: 'healthy' | 'watch' | 'warn' | 'critical';
};

function useAnalyzeShortcut(
  assetId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
) {
  // The shared keyboard map at AppShell already owns global guards
  // (input/textarea/contenteditable, modifier-key blocks). This hook
  // just contributes the screen-specific `a` / `A` handler.
  const map = useMemo<KeyMap>(() => {
    if (!assetId) return {} as KeyMap;
    const handler = () => goAnalyze(navigate, assetId);
    return {
      a: { description: 'Open Signal Analysis for the focused asset', handler },
      A: { description: 'Open Signal Analysis for the focused asset', handler },
    };
  }, [assetId, navigate]);
  useKeyboardMap(map);
}
